import React, { useState } from 'react';
import RingMark from './RingMark';
import LottieAnimation from '../LottieAnimation';
import portalDiscoBall from '../../assets/lottie/portal-disco-ball.json';
import { SignalIcon } from '../hand/icons';
import './clubsMotion.css';

/**
 * The two pinned rooms (clubs redesign handoff §1) — the rooms you're given, above the ones you
 * joined. Both are black cards that deliberately break the light chrome, and each keeps its own
 * world's colour: City Portal cyan, Portal Noir gold on near-black.
 */

const CYAN = '#00FFFF';
const GOLD = '#D4AF37';

const room = (phone) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minHeight: phone ? 220 : 260,
  padding: phone ? '18px 16px' : '22px 24px',
  boxSizing: 'border-box',
  textAlign: 'left',
  width: '100%',
  color: '#fff',
  border: 'none',
  font: 'inherit',
  cursor: 'pointer',
});

const kicker = { fontSize: 10, fontWeight: 700, letterSpacing: '0.2em' };
const cta = { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 50, borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: '0.18em' };

/** A pulsing magenta count — N Portals open in your rooms right now. */
function LivePill({ count }) {
  if (!count) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 999, background: '#FF00AA', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
      <span data-clb-motion style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'clbPulse 1.6s ease-in-out infinite' }} />
      {count} LIVE
    </span>
  );
}

export function TownSquareRoom({ onEnter, live = 0, phone = false }) {
  return (
    <button type="button" onClick={onEnter} style={{ ...room(phone), background: '#000' }} data-testid="pinned-square">
      {/* The prototype lays a square hero photo under this at 40%. There's no such asset in the
          app and the square's own banner is a per-town image we'd have to fetch, so the card
          carries a cyan wash instead — same reading, no extra request. */}
      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 90% at 18% 0%, rgba(0,255,255,0.22), transparent 62%), linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.2))' }} />
      <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ ...kicker, color: CYAN }}>PINNED · EVERYONE&apos;S HERE</span>
        <LivePill count={live} />
      </span>
      <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, marginTop: 'auto' }}>
        <RingMark size={52} spin={6} ripples={[0, 1.2, 2.4]} inset={4} faceBackground="#000">
          <span style={{ color: CYAN, display: 'flex' }}><SignalIcon size={20} /></span>
        </RingMark>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
          <span style={{ fontSize: phone ? 22 : 28, fontWeight: 800, letterSpacing: '0.06em', color: CYAN, lineHeight: 1 }}>CITY PORTAL</span>
          <span style={{ fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,.75)' }}>GEO-GRANTED · LOUD ON PURPOSE</span>
        </span>
      </span>
      <span style={{ ...cta, background: CYAN, color: '#000' }}>ENTER THE CITY PORTAL →</span>
    </button>
  );
}

export function PortalNoirRoom({ onEnter, isMember = false, phone = false }) {
  const [hot, setHot] = useState(false);
  return (
    <button
      type="button"
      onClick={onEnter}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      style={{ ...room(phone), background: '#0A0A0A' }}
      data-testid="pinned-noir"
    >
      {/* A gold sheen crossing the card every 5s — the club's one piece of movement. */}
      <span
        aria-hidden="true"
        data-clb-motion
        style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '25%', background: 'linear-gradient(100deg, rgba(212,175,55,0), rgba(212,175,55,.16), rgba(212,175,55,0))', animation: 'clbShine 5s ease-in-out infinite', pointerEvents: 'none' }}
      />
      <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ ...kicker, color: GOLD }}>PINNED · INVITE ONLY</span>
        {isMember && (
          <span style={{ padding: '4px 9px', borderRadius: 999, border: `1.5px solid ${GOLD}`, color: GOLD, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>MEMBER</span>
        )}
      </span>
      <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, marginTop: 'auto' }}>
        {/* The ball hangs from its own top and swings ±5°. The rainbow Lottie is filtered gold
            here the way .portal-disco does inside .portal-scope — this card lives outside it. */}
        <span
          data-clb-motion
          style={{
            flexShrink: 0,
            display: 'flex',
            transformOrigin: '50% 0',
            animation: 'clbSwing 4s ease-in-out infinite',
            filter: 'grayscale(1) sepia(1) saturate(2.6) hue-rotate(2deg) brightness(0.96) drop-shadow(0 0 6px rgba(201, 162, 75, 0.45))',
          }}
        >
          <LottieAnimation animationData={portalDiscoBall} width={52} height={52} preserveAspectRatio="xMidYMid meet" />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
          <span style={{ fontSize: phone ? 22 : 28, fontWeight: 500, letterSpacing: '0.34em', color: '#fff', lineHeight: 1 }}>PORTAL NOIR</span>
          <span style={{ fontSize: 10, letterSpacing: '0.14em', color: GOLD }}>A NETWORK OF REMARKABLE PEOPLE</span>
        </span>
      </span>
      <span style={{ ...cta, border: `1.5px solid ${GOLD}`, fontWeight: 600, letterSpacing: '0.24em', background: hot ? GOLD : 'transparent', color: hot ? '#000' : GOLD, transition: 'background-color .2s, color .2s' }}>
        ENTER PORTAL NOIR
      </span>
    </button>
  );
}
